import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Globe2, DownloadCloud, UserPlus, ShieldCheck, Crown, Rocket,
  Loader2, ArrowLeft, ArrowRight, CheckCircle2, AlertTriangle, Link2, Copy,
} from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { PageHeader, Pill } from "./ui";

const STEPS = [
  { icon: Globe2, title: "ENTER WEBSITE", desc: "Paste company website URL to get started." },
  { icon: DownloadCloud, title: "AUTO FETCH DETAILS", desc: "System automatically fetches public company information." },
  { icon: UserPlus, title: "ADD CONTACT DETAILS", desc: "Enter only person name, designation & phone number." },
  { icon: ShieldCheck, title: "REVIEW & VERIFY", desc: "Review company profile and verify the information." },
  { icon: Crown, title: "CHOOSE ACCESS TYPE", desc: "Choose Paid Membership or Admin Approved (Free Access)." },
  { icon: Rocket, title: "ACTIVATE COMPANY", desc: "Company gets activated and is ready to connect." },
];

const inputCls = "w-full border border-[#D9E2EF] rounded-sm px-3 py-2.5 text-sm text-[#0B1B33] bg-white focus:outline-none focus:border-[#2563EB] transition-colors";
const labelCls = "block text-[10px] font-mono uppercase tracking-widest text-[#7C8AA3] mb-1.5";
const btnPrimary = "inline-flex items-center gap-2 bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-xs font-semibold tracking-wide px-5 py-2.5 rounded-sm transition-colors disabled:opacity-50";
const btnGhost = "inline-flex items-center gap-2 border border-[#D9E2EF] text-[#43546D] hover:border-[#2563EB] hover:text-[#2563EB] text-xs font-semibold tracking-wide px-5 py-2.5 rounded-sm transition-colors";

export default function AdminRegisterCompany() {
  const [step, setStep] = useState(1);
  const [website, setWebsite] = useState("");
  const [fetching, setFetching] = useState(false);
  const [fetched, setFetched] = useState(null);
  const [form, setForm] = useState({ company_name: "", industry: "", city: "", country: "", description: "", contact_email: "" });
  const [contact, setContact] = useState({ name: "", designation: "", phone: "" });
  const [accessType, setAccessType] = useState("");
  const [plan, setPlan] = useState("");
  const [plans, setPlans] = useState([]);
  const [notes, setNotes] = useState("");
  const [activating, setActivating] = useState(false);
  const [result, setResult] = useState(null);
  const [payLink, setPayLink] = useState(null);
  const [linkWorking, setLinkWorking] = useState(false);

  useEffect(() => { api.get("/membership/plans").then((r) => setPlans(r.data || [])).catch(() => {}); }, []);

  const fetchDetails = async () => {
    setFetching(true);
    try {
      const { data } = await api.post("/admin/register-company/fetch", { website });
      setFetched(data);
      setForm({
        company_name: data.company_name_guess || "",
        industry: "", city: "", country: data.country || "",
        description: data.description || "", contact_email: data.contact_email || "",
      });
      setStep(2);
      if (data.duplicate) toast.warning(`Already registered: ${data.duplicate.company_name} (${data.duplicate.status})`);
      else toast.success(data.website_verified ? "Website verified — details auto-fetched" : "Website unreachable — fill details manually");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setFetching(false); }
  };

  const activate = async () => {
    setActivating(true);
    try {
      const { data } = await api.post("/admin/register-company/activate", {
        company_name: form.company_name, website, industry: form.industry, description: form.description,
        city: form.city, country: form.country,
        contact_name: contact.name, contact_designation: contact.designation, contact_phone: contact.phone,
        contact_email: form.contact_email, access_type: accessType, plan, notes,
      });
      setResult(data);
      toast.success("Company activated");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setActivating(false); }
  };

  const sharePaymentLink = async () => {
    setLinkWorking(true);
    try {
      const { data } = await api.post("/admin/payment-links", { company_token: result.token });
      setPayLink(`${window.location.origin}${data.path}`);
      toast.success("Payment link ready — share it with the company");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setLinkWorking(false); }
  };

  const reset = () => {
    setPayLink(null);
    setStep(1); setWebsite(""); setFetched(null); setResult(null); setAccessType(""); setPlan(""); setNotes("");
    setForm({ company_name: "", industry: "", city: "", country: "", description: "", contact_email: "" });
    setContact({ name: "", designation: "", phone: "" });
  };

  const canNext = () => {
    if (step === 1) return false;
    if (step === 2) return form.company_name.trim().length > 1;
    if (step === 5) return !!accessType && (accessType !== "PAID_MEMBERSHIP" || !!plan);
    return true;
  };

  const fld = (key, label, testid, props = {}) => (
    <div>
      <label className={labelCls}>{label}</label>
      <input value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} data-testid={testid} className={inputCls} {...props} />
    </div>
  );

  return (
    <div data-testid="page-register-company">
      <PageHeader kicker="Membership · Smart. Simple. Automated." title="REGISTER ANY COMPANY"
        sub="Minimum input by admin · Maximum automation · Complete control — six steps from website URL to activated company." />

      {/* STEP INDICATOR */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2.5 mb-6" data-testid="rc-steps">
        {STEPS.map((s, i) => (
          <div key={s.title} data-testid={`rc-step-${i + 1}`}
            className={`adm-card p-3.5 text-center transition-colors ${step === i + 1 ? "border-[#2563EB] ring-1 ring-[#2563EB]/30" : step > i + 1 ? "border-emerald-300" : ""}`}>
            <span className={`w-6 h-6 mx-auto rounded-full text-[11px] font-bold font-mono flex items-center justify-center ${step > i + 1 ? "bg-emerald-600 text-white" : step === i + 1 ? "bg-[#2563EB] text-white" : "bg-[#EEF2F9] text-[#7C8AA3]"}`}>{i + 1}</span>
            <p className="mt-2 text-[10px] font-bold tracking-wide text-[#0B1B33]">{s.title}</p>
            <p className="mt-1 text-[9.5px] text-[#93A3BC] leading-snug hidden md:block">{s.desc}</p>
          </div>
        ))}
      </div>

      <div className="adm-card p-6 md:p-8" data-testid="rc-panel">
        {/* STEP 1 — ENTER WEBSITE */}
        {step === 1 && (
          <div className="max-w-xl">
            <p className="adm-kicker mb-1.5">Step 1 — Enter Website</p>
            <h2 className="text-lg font-bold text-[#0B1B33]">Paste the company website URL</h2>
            <div className="mt-5 flex gap-2">
              <input value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="e.g. abcindustries.com"
                data-testid="rc-website-input" className={inputCls}
                onKeyDown={(e) => e.key === "Enter" && website.trim() && fetchDetails()} />
              <button onClick={fetchDetails} disabled={fetching || !website.trim()} data-testid="rc-fetch-btn" className={btnPrimary}>
                {fetching ? <Loader2 size={14} className="animate-spin" /> : <DownloadCloud size={14} />} FETCH DETAILS
              </button>
            </div>
            <p className="mt-3 text-[11px] text-[#93A3BC]">The system verifies the website live and auto-fetches public company information from the company's own site.</p>
          </div>
        )}

        {/* STEP 2 — AUTO FETCHED INFO (editable) */}
        {step === 2 && fetched && (
          <div>
            <div className="flex flex-wrap items-center gap-3 mb-5">
              <p className="adm-kicker">Step 2 — Auto Fetched Information</p>
              <span data-testid="rc-verify-badge">
                {fetched.website_verified
                  ? <Pill value="VERIFIED" />
                  : <span className="adm-pill text-red-700 border-red-300 bg-red-50">UNREACHABLE · HTTP {fetched.http_status}</span>}
              </span>
              {fetched.duplicate && (
                <span className="adm-pill text-amber-700 border-amber-300 bg-amber-50" data-testid="rc-duplicate-warning">
                  <AlertTriangle size={10} className="inline mr-1" />DUPLICATE — {fetched.duplicate.company_name}
                </span>
              )}
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {fld("company_name", "Company Name *", "rc-company-name-input")}
              {fld("industry", "Industry", "rc-industry-input", { placeholder: "e.g. Manufacturing" })}
              {fld("city", "Headquarters / City", "rc-city-input")}
              {fld("country", "Country", "rc-country-input")}
              {fld("contact_email", "Public Business Email", "rc-contact-email-input", { placeholder: "info@company.com" })}
              <div>
                <label className={labelCls}>Website</label>
                <input value={fetched.website} disabled className={`${inputCls} bg-[#F8FAFE] text-[#7C8AA3]`} data-testid="rc-website-readonly" />
              </div>
            </div>
            <div className="mt-4">
              <label className={labelCls}>Description</label>
              <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                data-testid="rc-description-input" className={`${inputCls} resize-none`} placeholder="What the company does…" />
            </div>
            <p className="mt-3 text-[11px] text-[#93A3BC]">All information is editable before activation.</p>
          </div>
        )}

        {/* STEP 3 — CONTACT DETAILS */}
        {step === 3 && (
          <div className="max-w-xl">
            <p className="adm-kicker mb-1.5">Step 3 — Add Contact Details</p>
            <h2 className="text-lg font-bold text-[#0B1B33]">Primary contact person</h2>
            <p className="mt-1 text-[12px] text-[#7C8AA3]">Enter only person name, designation & phone number — recorded as admin-provided, never scraped.</p>
            <div className="mt-5 space-y-4">
              <div>
                <label className={labelCls}>Person Name</label>
                <input value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} data-testid="rc-contact-name-input" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Designation</label>
                <input value={contact.designation} onChange={(e) => setContact({ ...contact, designation: e.target.value })} data-testid="rc-contact-designation-input" className={inputCls} placeholder="e.g. Director" />
              </div>
              <div>
                <label className={labelCls}>Phone Number</label>
                <input value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} data-testid="rc-contact-phone-input" className={inputCls} placeholder="+91 …" />
              </div>
            </div>
          </div>
        )}

        {/* STEP 4 — REVIEW & VERIFY */}
        {step === 4 && (
          <div data-testid="rc-review-panel">
            <p className="adm-kicker mb-1.5">Step 4 — Review & Verify</p>
            <div className="grid md:grid-cols-2 gap-6 mt-4">
              <div className="border border-[#E4EAF3] rounded-sm p-5">
                <p className="text-base font-bold text-[#0B1B33]">{form.company_name}</p>
                <p className="text-[12px] text-[#2563EB] font-mono mt-0.5">{fetched?.website}</p>
                <div className="mt-4 space-y-2.5 text-[12.5px]">
                  {[["Industry", form.industry], ["Location", [form.city, form.country].filter(Boolean).join(", ")],
                    ["Public Email", form.contact_email], ["Description", form.description]].map(([k, v]) => (
                    <div key={k} className="flex justify-between gap-4 border-b border-[#EEF2F9] pb-2">
                      <span className="text-[#7C8AA3] font-mono text-[10px] uppercase tracking-widest shrink-0">{k}</span>
                      <span className="text-[#22355A] text-right">{v || "—"}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="border border-[#E4EAF3] rounded-sm p-5">
                <p className="adm-kicker mb-3">Verification</p>
                <div className="flex items-center gap-2.5">
                  {fetched?.website_verified
                    ? <><CheckCircle2 size={16} className="text-emerald-600" /><span className="text-[13px] font-semibold text-emerald-700">Website verified live (HTTP {fetched.http_status})</span></>
                    : <><AlertTriangle size={16} className="text-red-500" /><span className="text-[13px] font-semibold text-red-600">Website unreachable — will be activated as unverified</span></>}
                </div>
                <p className="adm-kicker mt-6 mb-3">Primary Contact</p>
                <p className="text-[13px] text-[#22355A]">{contact.name || "—"}{contact.designation ? ` · ${contact.designation}` : ""}</p>
                <p className="text-[12px] font-mono text-[#7C8AA3] mt-1">{contact.phone || "—"}</p>
              </div>
            </div>
          </div>
        )}

        {/* STEP 5 — CHOOSE ACCESS TYPE */}
        {step === 5 && (
          <div>
            <p className="adm-kicker mb-1.5">Step 5 — Choose Access Type</p>
            <div className="grid md:grid-cols-2 gap-5 mt-4 max-w-3xl">
              <button onClick={() => setAccessType("PAID_MEMBERSHIP")} data-testid="rc-access-paid"
                className={`text-left border rounded-sm p-5 transition-colors ${accessType === "PAID_MEMBERSHIP" ? "border-[#2563EB] ring-1 ring-[#2563EB]/30 bg-[#F5F8FF]" : "border-[#E4EAF3] hover:border-[#2563EB]/50"}`}>
                <Crown size={20} className="text-[#2563EB]" />
                <p className="mt-3 font-bold text-[#0B1B33] tracking-wide">PAID MEMBERSHIP</p>
                <ul className="mt-2.5 space-y-1.5 text-[12px] text-[#5B6B87]">
                  <li>· Select membership plan</li><li>· Payment collected via membership checkout</li><li>· Access to premium benefits</li>
                </ul>
                {accessType === "PAID_MEMBERSHIP" && (
                  <select value={plan} onChange={(e) => setPlan(e.target.value)} data-testid="rc-plan-select"
                    className={`${inputCls} mt-4`} onClick={(e) => e.stopPropagation()}>
                    <option value="">Select plan…</option>
                    {plans.map((p) => <option key={p.name} value={p.name}>{`${p.name} — ₹${String(p.price).replace(/^₹\s*/, "")}`}</option>)}
                  </select>
                )}
              </button>
              <button onClick={() => { setAccessType("ADMIN_APPROVED_FREE"); setPlan(""); }} data-testid="rc-access-free"
                className={`text-left border rounded-sm p-5 transition-colors ${accessType === "ADMIN_APPROVED_FREE" ? "border-emerald-500 ring-1 ring-emerald-500/30 bg-emerald-50/50" : "border-[#E4EAF3] hover:border-emerald-400"}`}>
                <UserPlus size={20} className="text-emerald-600" />
                <p className="mt-3 font-bold text-[#0B1B33] tracking-wide">ADMIN APPROVED <span className="text-emerald-700">(FREE ACCESS)</span></p>
                <ul className="mt-2.5 space-y-1.5 text-[12px] text-[#5B6B87]">
                  <li>· No payment required</li><li>· Admin approval · internal notes optional</li><li>· Full access as per rules</li>
                </ul>
              </button>
            </div>
            <div className="mt-5 max-w-3xl">
              <label className={labelCls}>Internal Notes (optional)</label>
              <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="rc-notes-input" className={`${inputCls} resize-none`} />
            </div>
          </div>
        )}

        {/* STEP 6 — ACTIVATE */}
        {step === 6 && !result && (
          <div className="max-w-xl">
            <p className="adm-kicker mb-1.5">Step 6 — Activate Company</p>
            <h2 className="text-lg font-bold text-[#0B1B33]">{form.company_name}</h2>
            <p className="mt-1 text-[12.5px] text-[#5B6B87]">
              Access: <span className="font-semibold">{accessType === "PAID_MEMBERSHIP" ? `Paid Membership${plan ? ` · ${plan}` : ""}` : "Admin Approved (Free Access)"}</span>
              {" · "}Website: <span className={`font-semibold ${fetched?.website_verified ? "text-emerald-700" : "text-red-600"}`}>{fetched?.website_verified ? "VERIFIED" : "UNVERIFIED"}</span>
            </p>
            <p className="mt-3 text-[12px] text-[#93A3BC]">On activation the company record is persisted, an audit event is logged, and — when the website is verified with a public role-based business email — the company is listed in the directory.</p>
            <button onClick={activate} disabled={activating} data-testid="rc-activate-btn" className={`${btnPrimary} mt-6`}>
              {activating ? <Loader2 size={14} className="animate-spin" /> : <Rocket size={14} />} ACTIVATE COMPANY
            </button>
          </div>
        )}

        {/* SUCCESS */}
        {result && (
          <div className="text-center py-8" data-testid="rc-success-panel">
            <CheckCircle2 size={42} className="text-emerald-600 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-[#0B1B33]">COMPANY ACTIVATED</h2>
            <p className="font-mono text-[12px] text-[#2563EB] mt-1.5">{result.token}</p>
            <div className="mt-5 flex flex-wrap justify-center gap-2.5">
              <Pill value={result.status} />
              <Pill value={result.website_verified ? "VERIFIED" : "PENDING"} />
              <span className="adm-pill text-slate-600 border-slate-300 bg-slate-50" data-testid="rc-result-access">{result.access_type.replace(/_/g, " ")}</span>
              <span className="adm-pill text-slate-600 border-slate-300 bg-slate-50" data-testid="rc-result-payment">PAYMENT {result.payment_status}</span>
              {result.listed_in_directory && <Pill value="LIVE" />}
            </div>
            {result.access_type === "PAID_MEMBERSHIP" && (
              <div className="mt-6 border border-[#E4EAF3] rounded-sm p-5 max-w-lg mx-auto" data-testid="rc-payment-link-section">
                <p className="adm-kicker mb-3">Paid Membership — Share Payment Link</p>
                {!payLink ? (
                  <button onClick={sharePaymentLink} disabled={linkWorking} data-testid="rc-payment-link-btn" className={btnPrimary}>
                    {linkWorking ? <Loader2 size={14} className="animate-spin" /> : <Link2 size={14} />} GENERATE PAYMENT LINK
                  </button>
                ) : (
                  <div className="flex items-center gap-2 border border-[#2563EB]/40 bg-[#F5F8FF] rounded-sm px-3 py-2.5" data-testid="rc-payment-link-box">
                    <span className="font-mono text-[11px] text-[#2563EB] truncate flex-1 text-left" data-testid="rc-payment-link-url">{payLink}</span>
                    <button onClick={() => { navigator.clipboard.writeText(payLink); toast.success("Payment link copied"); }}
                      data-testid="rc-copy-payment-link-btn" className="text-[#2563EB] hover:text-[#1D4ED8] shrink-0"><Copy size={15} /></button>
                  </div>
                )}
                <p className="mt-2.5 text-[10.5px] text-[#93A3BC]">Send this link to the company — it opens a secure Stripe checkout for the selected plan. Payment status updates to PAID automatically on completion.</p>
              </div>
            )}
            <button onClick={reset} data-testid="rc-register-another-btn" className={`${btnGhost} mt-7`}>REGISTER ANOTHER COMPANY</button>
          </div>
        )}

        {/* NAV */}
        {!result && step > 1 && (
          <div className="flex justify-between mt-8 pt-5 border-t border-[#EEF2F9]">
            <button onClick={() => setStep(step - 1)} data-testid="rc-back-btn" className={btnGhost}><ArrowLeft size={13} /> BACK</button>
            {step < 6 && (
              <button onClick={() => canNext() && setStep(step + 1)} disabled={!canNext()} data-testid="rc-next-btn" className={btnPrimary}>NEXT <ArrowRight size={13} /></button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
